<template>
  <div>
    <div>
      <v-radio-group v-model="selectedShareType"
                     class="my-0"
                     hide-details
      >
        <v-radio v-if="isCurrentUserPm"
                 :value="SHARE_TYPE_OPTIONS.PUBLIC"
                 class="qa-share-radio"
                 :class="{ 'qa-share-radio--active': shareType === SHARE_TYPE_OPTIONS.PUBLIC}"
        >
          <template #label>
            <div class="d-flex flex-column ml-2">
              <span class="text--medium text--bold">
                {{ $t('project.question-answer.QuestionAnswerAddShare.publicTitle') }}
              </span>
              <span class="text-grey">
                {{ $t('project.question-answer.QuestionAnswerAddShare.publicSubtitle') }}
              </span>
            </div>
          </template>
        </v-radio>
        <v-radio :value="SHARE_TYPE_OPTIONS.CUSTOM"
                 class="qa-share-radio"
                 :class="{ 'qa-share-radio--active': shareType === SHARE_TYPE_OPTIONS.CUSTOM}"
        >
          <template #label>
            <div class="d-flex flex-column ml-2">
              <span class="text--medium text--bold">
                {{ $t('project.question-answer.QuestionAnswerAddShare.customTitle') }}
              </span>
              <span class="text-grey">
                {{ $t('project.question-answer.QuestionAnswerAddShare.customSubtitle') }}
              </span>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </div>

    <div v-if="selectedShareType === SHARE_TYPE_OPTIONS.CUSTOM" class="mt-4">
      <DocumentSharePicker :force-pm-selection="true" :force-current-user-selection="true"/>
    </div>

    <v-checkbox v-model="customMessageIsEnabled"
                hide-details
                class="mt-4 pt-0"
                :style="{'margin-left': '26px'}"
                :label="$t('project.question-answer.QuestionAnswerAddShare.sendCustomMessage')"
    />

    <div v-if="customMessageIsEnabled" class="mt-4">
      <VisualTextEditor v-model:value="notifyMessage"
                        qa-variant
                        counter="footer"
                        :errorMessages="notifyMessageErrors"
                        :placeholder="$t('project.question-answer.QuestionAnswerAddShare.customMessagePlaceholder')"
                        :max-length="NOTIFY_MESSAGE_MAX_LENGTH"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import { formFieldErrors } from '@/common/utils/form'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { maxEscapedLength } from '@/common/utils/validators'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 3000

export const validations = {
  customShareMessage: {
    maxLength: maxEscapedLength(NOTIFY_MESSAGE_MAX_LENGTH),
  },
}

export default {
  name: 'QuestionAnswerAddShare',
  components: { DocumentSharePicker, VisualTextEditor },
  props: {
    customMessage: {
      type: String,
      required: true,
    },
    customMessageValidation: {
      type: Object,
      required: true,
    },
    shareType: {
      type: String,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      NOTIFY_MESSAGE_MAX_LENGTH,
      SHARE_TYPE_OPTIONS,
      customMessageIsEnabled: !!this.customMessage.length,
    }
  },
  computed: {
    ...mapState('groups', ['groups']),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    selectedShareType: {
      get () { return this.shareType },
      set (value) { this.$emit('update:shareType', value) },
    },
    notifyMessage: {
      get () { return this.customMessage },
      set (value) { this.$emit('update:customMessage', value) },
    },
    notifyMessageErrors () {
      return formFieldErrors(
        this.customMessageValidation,
        [
          [
            'maxLength',
            this.$t('common.validations.maxLength', {
              fieldName: this.$t('project.question-answer.QuestionAnswerAddShare.customMessage'),
              maxLength: NOTIFY_MESSAGE_MAX_LENGTH,
            }),
          ],
        ],
      )
    },
  },
  watch: {
    customMessageIsEnabled (value) {
      if (!value) {
        this.notifyMessage = ''
      }
    },
  },
  beforeUnmount () {
    this.RESET_SELECTED_GROUPS_AND_MEMBERS()
  },
  async created () {
    // If question is being edited, do not fetch and set selected groups and members
    // they will be set from the parent component with the data from the question
    if (!this.isEditMode) {
      await this.initializeGroupsAndMembers()
    }
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    ...mapActions('groups', ['GET_GROUPS']),
    async initializeGroupsAndMembers () {
      await this.GET_GROUPS(this.roomMnemo)
      const pmGroup = this.groups.find(g => g.isPm)
      if (pmGroup) {
        this.INIT_SELECTED_GROUPS([pmGroup])
      }
      // Add current user to selected members
      this.INIT_SELECTED_MEMBERS([{ id: this.currentUserId }])
    },
  },
}
</script>

<style scoped lang="scss">
.qa-share-radio {
  padding: 16px 24px;

  &--active {
    background-color: rgb(var(--v-theme-primary-lighten-5));
  }
}

.custom-label-title {
  font-size: 16px;
  font-weight: 500;
}
</style>
